import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      gridTemplateColumns: '1fr',
    },
  },
  heatmap: {
    padding: theme.spacing(2),
    height: '100%',
  },
  metrics: {
    padding: theme.spacing(2),
    height: '100%',
  },
  table: {
    minWidth: 650,
  },
  tableCell: {
    padding: theme.spacing(1),
    textAlign: 'center',
  },
  headerCell: {
    backgroundColor: theme.palette.background.paper,
    fontWeight: 'bold',
  },
  metricCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.shape.borderRadius,
  },
  summaryCard: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
}));

const getColorForValue = (value, maxValue) => {
  const intensity = Math.round((value / maxValue) * 255);
  return `rgba(25, 118, 210, ${intensity / 255})`;
};

const MatrixVisualization = ({ data, metrics }) => {
  const classes = useStyles();

  const renderHeatmap = () => {
    const { rows, columns, values } = data;
    const maxValue = Math.max(...values.flat());

    return (
      <Paper className={classes.heatmap}>
        <Typography variant="h6" gutterBottom>
          Response Matrix
        </Typography>
        <TableContainer>
          <Table className={classes.table}>
            <TableHead>
              <TableRow>
                <TableCell className={`${classes.tableCell} ${classes.headerCell}`} />
                {columns.map((column) => (
                  <TableCell key={column} className={`${classes.tableCell} ${classes.headerCell}`}>
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, rowIndex) => (
                <TableRow key={row}>
                  <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                    {row}
                  </TableCell>
                  {columns.map((_, colIndex) => {
                    const value = values[rowIndex][colIndex];
                    return (
                      <TableCell
                        key={`${rowIndex}-${colIndex}`}
                        className={classes.tableCell}
                        style={{
                          backgroundColor: getColorForValue(value, maxValue),
                          color: value > maxValue / 2 ? 'white' : 'black',
                        }}
                      >
                        {value}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    );
  };

  const renderMetrics = () => {
    const { values } = data;
    const totalResponses = values.flat().reduce((sum, value) => sum + value, 0);
    const averageValue = totalResponses / (values.length * values[0].length);
    const maxValue = Math.max(...values.flat());
    const minValue = Math.min(...values.flat());

    return (
      <Paper className={classes.metrics}>
        <Typography variant="h6" gutterBottom>
          Statistics
        </Typography>
        <Box className={classes.metricCard}>
          <Typography variant="subtitle2">Total Responses</Typography>
          <Typography variant="h4">{totalResponses}</Typography>
        </Box>
        <Box className={classes.metricCard}>
          <Typography variant="subtitle2">Average Value</Typography>
          <Typography variant="h4">{averageValue.toFixed(1)}</Typography>
        </Box>
        <Box className={classes.metricCard}>
          <Typography variant="subtitle2">Response Rate</Typography>
          <Typography variant="h4">{metrics?.responseRate || 0}%</Typography>
        </Box>
        <Box className={classes.metricCard}>
          <Typography variant="subtitle2">Value Range</Typography>
          <Typography variant="h4">
            {minValue} - {maxValue}
          </Typography>
        </Box>
      </Paper>
    );
  };

  return (
    <Box className={classes.root}>
      {renderHeatmap()}
      {renderMetrics()}
    </Box>
  );
};

MatrixVisualization.propTypes = {
  data: PropTypes.shape({
    rows: PropTypes.arrayOf(PropTypes.string),
    columns: PropTypes.arrayOf(PropTypes.string),
    values: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)),
  }),
  metrics: PropTypes.shape({
    responseRate: PropTypes.number,
  }),
};

export default MatrixVisualization; 